/**
 * Payment Processor Configuration Page
 *
 * Admin page for configuring payment processors (USAePay, Dejavoo) for clinics.
 */

import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';
import { PaymentProcessorType } from '@chiroup/core/types/PaymentProcessor.type';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

interface PaymentProcessorConfigProps {
  clinic: {
    ID: number;
    name: string;
  };
}

interface ProcessorConfig {
  id: number;
  processor: PaymentProcessorType;
  isActive: boolean;
  isDefault: boolean;
  locationId: number | null;
  createdAt: number;
}

interface DejavooCredentials {
  tpn: string;
  authKey: string;
  registerId?: string;
  ecommToken?: string;
}

interface Terminal {
  id: number;
  deviceKey: string;
  deviceName: string;
  deviceType: string | null;
  status: string | null;
  locationId: number | null;
}

const PaymentProcessorConfig: React.FC<PaymentProcessorConfigProps> = ({ clinic }) => {
  const queryClient = useQueryClient();
  const [isAddingProcessor, setIsAddingProcessor] = useState(false);
  const [newProcessorType, setNewProcessorType] = useState<PaymentProcessorType>(
    PaymentProcessorType.Dejavoo
  );
  const [dejavooForm, setDejavooForm] = useState<DejavooCredentials>({
    tpn: '',
    authKey: '',
    registerId: '',
    ecommToken: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Query for processor configurations
  const configsQuery = useQuery<{ configs: ProcessorConfig[]; terminals: Terminal[] }>(
    ['paymentProcessorConfigs', clinic.ID],
    async () => {
      return ChiroUpAPI.get('api', `/clinic/${clinic.ID}/payment-processors`, {});
    },
    {
      enabled: !!clinic.ID,
      refetchOnWindowFocus: false,
    }
  );

  // Mutation for adding/updating processor
  const saveProcessorMutation = useMutation<
    { success: boolean; configId: number; credentialId: number },
    Error,
    {
      processor: PaymentProcessorType;
      credentials: DejavooCredentials;
      isDefault?: boolean;
      locationId?: number;
    }
  >(
    async params => {
      return ChiroUpAPI.post('api', `/clinic/${clinic.ID}/payment-processor`, {
        body: params,
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['paymentProcessorConfigs', clinic.ID]);
        setIsAddingProcessor(false);
        setDejavooForm({ tpn: '', authKey: '', registerId: '', ecommToken: '' });
        setSuccess('Payment processor configured successfully');
        setTimeout(() => setSuccess(null), 3000);
      },
      onError: error => {
        setError(error.message || 'Failed to save processor configuration');
      },
    }
  );

  // Mutation for deactivating processor
  const deactivateProcessorMutation = useMutation<{ success: boolean }, Error, number>(
    async configId => {
      return ChiroUpAPI.del('api', `/clinic/${clinic.ID}/payment-processor/${configId}`, {});
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['paymentProcessorConfigs', clinic.ID]);
        setSuccess('Payment processor deactivated');
        setTimeout(() => setSuccess(null), 3000);
      },
      onError: error => {
        setError(error.message || 'Failed to deactivate processor');
      },
    }
  );

  // Mutation for setting default processor
  const setDefaultMutation = useMutation<{ success: boolean }, Error, number>(
    async configId => {
      return ChiroUpAPI.put(
        'api',
        `/clinic/${clinic.ID}/payment-processor/${configId}/default`,
        {}
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['paymentProcessorConfigs', clinic.ID]);
        setSuccess('Default processor updated');
        setTimeout(() => setSuccess(null), 3000);
      },
      onError: error => {
        setError(error.message || 'Failed to set default processor');
      },
    }
  );

  const handleSaveProcessor = () => {
    setError(null);

    if (newProcessorType === 'dejavoo') {
      if (!dejavooForm.tpn || !dejavooForm.authKey) {
        setError('TPN and Auth Key are required for Dejavoo');
        return;
      }
    }

    saveProcessorMutation.mutate({
      processor: newProcessorType,
      credentials: dejavooForm,
      isDefault: (configsQuery.data?.configs || []).length === 0,
    });
  };

  const getProcessorDisplayName = (processor: PaymentProcessorType) => {
    switch (processor) {
      case 'usaepay':
        return 'USAePay';
      case 'dejavoo':
        return 'Dejavoo Spin';
      default:
        return processor;
    }
  };

  const configs = configsQuery.data?.configs || [];
  const terminals = configsQuery.data?.terminals || [];

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-4">
        <h2 className="text-lg font-medium text-gray-900">Payment Processor Configuration</h2>
        <p className="mt-1 text-sm text-gray-500">
          Configure payment processors for {clinic.name}. You can have multiple processors active
          simultaneously for gradual migration.
        </p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="rounded-md bg-green-50 p-4">
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={() => setError(null)} className="mt-2 text-sm text-red-600 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Current Configurations */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-md font-medium text-gray-900 mb-4">Active Processors</h3>

        {configsQuery.isLoading ? (
          <p className="text-sm text-gray-500">Loading configurations...</p>
        ) : configs.length === 0 ? (
          <p className="text-sm text-gray-500">No payment processors configured.</p>
        ) : (
          <div className="space-y-4">
            {configs.map(config => (
              <div
                key={config.id}
                className="border border-gray-200 rounded-lg p-4 flex items-center justify-between"
              >
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-900">
                      {getProcessorDisplayName(config.processor)}
                    </span>
                    {config.isDefault && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        Default
                      </span>
                    )}
                    {config.isActive ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                        Inactive
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Configured {new Date(config.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  {!config.isDefault && config.isActive && (
                    <button
                      onClick={() => setDefaultMutation.mutate(config.id)}
                      disabled={setDefaultMutation.isLoading}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Set as Default
                    </button>
                  )}
                  {config.isActive && (
                    <button
                      onClick={() => deactivateProcessorMutation.mutate(config.id)}
                      disabled={deactivateProcessorMutation.isLoading}
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      Deactivate
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Terminals Section */}
      {terminals.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-md font-medium text-gray-900 mb-4">Registered Terminals</h3>
          <div className="space-y-3">
            {terminals.map(terminal => (
              <div
                key={terminal.id}
                className="border border-gray-200 rounded-lg p-3 flex items-center justify-between"
              >
                <div>
                  <span className="font-medium text-gray-900">{terminal.deviceName}</span>
                  <span className="text-sm text-gray-500 ml-2">({terminal.deviceKey})</span>
                  <p className="text-sm text-gray-500">
                    Type: {terminal.deviceType || 'Unknown'} | Status:{' '}
                    {terminal.status || 'Unknown'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add New Processor */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-md font-medium text-gray-900 mb-4">Add Payment Processor</h3>

        {!isAddingProcessor ? (
          <button
            onClick={() => setIsAddingProcessor(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
          >
            Add Processor
          </button>
        ) : (
          <div className="space-y-4">
            {/* Processor Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700">Processor Type</label>
              <select
                value={newProcessorType}
                onChange={e => setNewProcessorType(e.target.value as PaymentProcessorType)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option value="dejavoo">Dejavoo Spin</option>
                <option value="usaepay">USAePay (Legacy)</option>
              </select>
            </div>

            {/* Dejavoo Configuration */}
            {newProcessorType === 'dejavoo' && (
              <div className="space-y-4 border-t border-gray-200 pt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    TPN (Terminal Profile Number)
                  </label>
                  <input
                    type="text"
                    value={dejavooForm.tpn}
                    onChange={e => setDejavooForm({ ...dejavooForm, tpn: e.target.value })}
                    placeholder="Enter TPN (10-12 characters)"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    The Terminal Profile Number from your Dejavoo merchant portal
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Auth Key</label>
                  <input
                    type="text"
                    value={dejavooForm.authKey}
                    onChange={e => setDejavooForm({ ...dejavooForm, authKey: e.target.value })}
                    placeholder="Enter 10-character auth key"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    10-character auth key from your Dejavoo Spin portal
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Register ID (Optional)
                  </label>
                  <input
                    type="text"
                    value={dejavooForm.registerId || ''}
                    onChange={e => setDejavooForm({ ...dejavooForm, registerId: e.target.value })}
                    placeholder="Optional register identifier"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Optional register ID for multi-register environments
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Ecomm Token (for Manual Entry)
                  </label>
                  <input
                    type="text"
                    value={dejavooForm.ecommToken || ''}
                    onChange={e => setDejavooForm({ ...dejavooForm, ecommToken: e.target.value })}
                    placeholder="JWT token for card-not-present transactions"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    JWT token from iPOSpays Settings → Generate Ecom/TOP Merchant Keys (enables
                    card-not-present transactions)
                  </p>
                </div>
              </div>
            )}

            {/* USAePay note */}
            {newProcessorType === 'usaepay' && (
              <div className="border-t border-gray-200 pt-4">
                <p className="text-sm text-gray-600">
                  USAePay is configured through the existing clinic API keys system. This option
                  sets USAePay as the default processor for this clinic.
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center space-x-3 pt-4">
              <button
                onClick={handleSaveProcessor}
                disabled={saveProcessorMutation.isLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {saveProcessorMutation.isLoading ? 'Saving...' : 'Save Processor'}
              </button>
              <button
                onClick={() => {
                  setIsAddingProcessor(false);
                  setError(null);
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Migration Notes */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-yellow-800">Migration Notes</h4>
        <ul className="mt-2 text-sm text-yellow-700 list-disc list-inside space-y-1">
          <li>Both processors can be active simultaneously during migration</li>
          <li>The default processor is used for new payments</li>
          <li>Existing USAePay transactions will continue to work</li>
          <li>Terminals must be registered in the clinic's payment settings</li>
        </ul>
      </div>
    </div>
  );
};

export default PaymentProcessorConfig;
